import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement, Fragment } from "react";
import redraft from "redraft";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import linkify from "../../linkify";
import { formatURL } from "../parse/text";
import UrlLink from "./UrlLink";
import formatToRawDraftJS from "./draftjs";
const linkStrategy = (contentBlock, callback)=>{
    const links = linkify.match(contentBlock.getText());
    if (links) links.forEach((link)=>{
        callback(link.index, link.lastIndex);
    });
};
const styles = {
    bold: {
        fontWeight: "bold"
    },
    italic: {
        fontStyle: "italic"
    },
    strikethrough: {
        textDecoration: "line-through"
    },
    blockquote: {
        margin: 0,
        borderLeft: "2px solid #bdbdbd",
        paddingLeft: 16,
        fontStyle: "italic",
        color: "#757575"
    },
    code: {
        color: "#d72b3f",
        fontSize: "75%",
        borderRadius: "6px",
        backgroundColor: "#f7f7f9",
        padding: "2px 4px",
        border: "1px solid #e1e1e8",
        fontFamily: "ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace"
    }
};
const Anchor = styled(UrlLink)`
  color: #4991cc;
  &:hover {
    text-decoration: underline;
  }
`;
const codeFont = css`
  font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas,
    Liberation Mono, monospace;
  font-size: 75%;
`;
const Pre = styled.pre`
  ${codeFont}
  margin: 8px 0;
`;
const Code = styled.code`
  display: block;
  ${codeFont};
  padding: 8px;
  white-space: pre-wrap;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  background: #f5f5f5;
`;
const Mention = styled(Link)`
  color: #0576b9;
  background: #eaf5fc;

  padding: 2px 4px;
  border-radius: 4px;

  &:hover {
    background: #e0f0fa;
    color: #005e99;
  }
`;
const normalBlock = (Tag1, Tag2)=>// eslint-disable-next-line react/display-name
    (children, { keys  })=>/*#__PURE__*/ createElement(Tag1, {
            key: keys[0]
        }, children.map((child, index)=>Tag2 ? /*#__PURE__*/ createElement(Tag2, {}, child) : [
                index > 0 ? /*#__PURE__*/ _jsx("br", {}, index) : null,
                child
            ]));
const SUPPORT_IFRAME = false;
function convertEmbed(data) {
    const html = data === null || data === void 0 ? void 0 : data.html;
    if (typeof html !== "string") {
        console.error("Could not understand embed", data);
        return null;
    }
    // Example HTML:
    // <div class="iframely-embed">
    //   <div class="iframely-responsive" style="height: 140px; padding-bottom: 0;">
    //     <iframe
    //       src="//cdn.iframe.ly/api/iframe?url=https%3A%2F%2Fremirror.io%2Fdocs%2Fshowcase%2Fsocial%3Fiframely%3Dless&amp;key=7e72882d26e4c8a83aafa4a3b86028c8&amp;app=1"
    //       allowfullscreen
    //     ></iframe>
    //   </div>
    // </div>
    const iframeMatches = /<iframe src="([^"]+)"/.exec(html);
    if (iframeMatches) {
        const iframeUrl = iframeMatches[1];
        if (SUPPORT_IFRAME && /^(?:https?:)?\/\/cdn.iframe.ly\//.test(iframeUrl)) // Safe
        return /*#__PURE__*/ _jsx("iframe", {
            title: "Embed",
            src: iframeUrl,
            allowFullScreen: true
        });
        else return /*#__PURE__*/ _jsx("a", {
            href: iframeUrl,
            target: "_blank",
            rel: "noreferrer noopener",
            children: "Previously Embedded Content"
        });
    }
    // Could not safely extract embed.
    console.error("Could not process embedded html");
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: "[Unable to convert embed]"
    });
}
export function formatRawDraftJSToSemanticReact(raw) {
    return redraft(raw, {
        inline: {
            BOLD: (children, { key  })=>/*#__PURE__*/ _jsx("strong", {
                    style: styles.bold,
                    children: children
                }, key),
            ITALIC: (children, { key  })=>/*#__PURE__*/ _jsx("em", {
                    style: styles.italic,
                    children: children
                }, key),
            STRIKETHROUGH: (children, { key  })=>/*#__PURE__*/ _jsx("s", {
                    style: styles.strikethrough,
                    children: children
                }, key),
            CODE: (children, { key  })=>/*#__PURE__*/ _jsx("code", {
                    style: styles.code,
                    children: children
                }, key)
        },
        blocks: {
            "header-one": normalBlock("h1"),
            "header-two": normalBlock("h2"),
            "header-three": normalBlock("h3"),
            "header-four": normalBlock("h4"),
            "header-five": normalBlock("h5"),
            "header-six": normalBlock("h6"),
            "unordered-list-item": normalBlock("ul", "li"),
            "ordered-list-item": normalBlock("ol", "li"),
            atomic: (children, { key  })=>/*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        children,
                        /*#__PURE__*/ _jsx("br", {})
                    ]
                }, key),
            unstyled: normalBlock("span"),
            blockquote: (children, { keys  })=>/*#__PURE__*/ _jsx("blockquote", {
                    style: styles.blockquote,
                    children: children.map((child, index)=>[
                            // eslint-disable-next-line react/no-array-index-key
                            index > 0 ? /*#__PURE__*/ _jsx("br", {}, index) : null,
                            child, 
                        ])
                }, keys[keys.length - 1]),
            "code-block": (children, { keys  })=>/*#__PURE__*/ _jsx(Pre, {
                    children: /*#__PURE__*/ _jsx(Code, {
                        children: children.map((child, index)=>[
                                // eslint-disable-next-line react/no-array-index-key
                                index > 0 ? /*#__PURE__*/ _jsx("br", {}, index) : null,
                                child, 
                            ])
                    })
                }, keys[keys.length - 1])
        },
        entities: {
            mention: (children, data, { key  })=>/*#__PURE__*/ _jsx(Mention, {
                    to: `/${data.mention.name}`,
                    children: children
                }, key),
            HORIZONTAL_RULE: (_children, _data, { key  })=>/*#__PURE__*/ _jsx("hr", {}, key),
            EMBED: (children, data, { key  })=>/*#__PURE__*/ _jsx("div", {
                    className: "embed",
                    children: convertEmbed(data) || children
                }, key)
        },
        decorators: [
            {
                strategy: linkStrategy,
                component: ({ decoratedText , key  })=>/*#__PURE__*/ _jsx(Anchor, {
                        url: decoratedText,
                        children: formatURL(decoratedText)
                    }, key)
            }, 
        ]
    });
}
function formatRawDraftJSToDivsAndSpansReact(raw) {
    return redraft(raw, {
        inline: {
            BOLD: (children, { key  })=>/*#__PURE__*/ _jsx("span", {
                    style: styles.bold,
                    children: children
                }, key),
            ITALIC: (children, { key  })=>/*#__PURE__*/ _jsx("span", {
                    style: styles.italic,
                    children: children
                }, key),
            STRIKETHROUGH: (children, { key  })=>/*#__PURE__*/ _jsx("span", {
                    style: styles.strikethrough,
                    children: children
                }, key),
            CODE: (children, { key  })=>/*#__PURE__*/ _jsx("span", {
                    style: styles.code,
                    children: children
                }, key)
        },
        blocks: {
            unstyled: (children, { keys  })=>{
                return /*#__PURE__*/ _jsx("span", {
                    children: children.map((child, index)=>[
                            child,
                            // eslint-disable-next-line react/no-array-index-key
                            children.length - 1 === index ? null : /*#__PURE__*/ _jsx("br", {}, index), 
                        ])
                }, keys[0]);
            },
            blockquote: (children, { keys  })=>/*#__PURE__*/ _jsx("blockquote", {
                    style: styles.blockquote,
                    children: children.map((child, index)=>[
                            child,
                            /*#__PURE__*/ _jsx("br", {}, index)
                        ])
                }, keys[keys.length - 1]),
            "code-block": (children, { keys  })=>/*#__PURE__*/ _jsx(Pre, {
                    children: /*#__PURE__*/ _jsx(Code, {
                        children: children.map((child, index)=>[
                                child,
                                /*#__PURE__*/ _jsx("br", {}, index)
                            ])
                    })
                }, keys[keys.length - 1])
        },
        entities: {
            mention: (children, data, { key  })=>/*#__PURE__*/ _jsx(Mention, {
                    to: `/${data.mention.name}`,
                    children: children
                }, key)
        },
        decorators: [
            {
                strategy: linkStrategy,
                component: ({ decoratedText , key  })=>/*#__PURE__*/ _jsx(Anchor, {
                        url: decoratedText,
                        children: formatURL(decoratedText)
                    }, key)
            }, 
        ]
    });
}
export default function formatToReact(text, mode) {
    const raw = formatToRawDraftJS(text, mode);
    return formatRawDraftJSToDivsAndSpansReact(raw);
};
